import React, { useState, useRef, useEffect, useCallback } from 'react';
import * as faceapi from 'face-api.js';
import { timeEntriesApi, employeesApi } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Clock,
  Camera,
  LogIn,
  LogOut,
  CheckCircle,
  Loader2,
  Building2,
  User,
  ArrowLeft,
  RefreshCw,
  AlertCircle,
  Lock,
  Eye,
  EyeOff,
  UserCheck,
  Coffee,
  Play,
  Pause,
} from 'lucide-react';
import { format } from 'date-fns';
import { useTimeFormat } from '../utils/timeFormat';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_showtime-staff/artifacts/1ijkahsm_logo%20jf.png";

const Kiosk = () => {
  const fmtTime = useTimeFormat();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [step, setStep] = useState('lookup'); // lookup, passcode, setPasscode, action, camera, managerLogin
  const [employeeId, setEmployeeId] = useState('');
  const [passcode, setPasscode] = useState('');
  const [newPasscode, setNewPasscode] = useState('');
  const [confirmPasscode, setConfirmPasscode] = useState('');
  const [showPasscode, setShowPasscode] = useState(false);
  const [foundEmployee, setFoundEmployee] = useState(null);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [clockAction, setClockAction] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');
  const [cameraError, setCameraError] = useState('');
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraUnavailable, setCameraUnavailable] = useState(false); // Track if camera is unavailable
  const [faceDetected, setFaceDetected] = useState(false); // Face detection status
  const [faceModelLoaded, setFaceModelLoaded] = useState(false); // Face model loaded
  const [detectingFace, setDetectingFace] = useState(false); // Currently detecting face
  // Break tracking state
  const [activeEntry, setActiveEntry] = useState(null); // Current active time entry
  const [onBreak, setOnBreak] = useState(false); // Currently on break
  const [breakCount, setBreakCount] = useState(0); // Number of breaks taken
  const [totalBreakMinutes, setTotalBreakMinutes] = useState(0); // Total break time
  // Manager login state
  const [managerEmail, setManagerEmail] = useState('');
  const [managerPassword, setManagerPassword] = useState('');
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const faceDetectionIntervalRef = useRef(null);

  // Load face detection model on mount
  useEffect(() => {
    const loadModels = async () => {
      try {
        await faceapi.nets.tinyFaceDetector.loadFromUri('/models');
        setFaceModelLoaded(true);
      // Face detection model loaded
      } catch (err) {
        console.warn('Face detection model failed to load:', err);
        // Continue without face detection - it's an enhancement, not critical
      }
    };
    loadModels();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => {
      clearInterval(timer);
      stopCamera();
      if (faceDetectionIntervalRef.current) {
        clearInterval(faceDetectionIntervalRef.current);
      }
    };
  }, []);

  const handleLookup = async () => {
    if (!employeeId.trim()) {
      setError('Please enter your Employee ID');
      return;
    }
    setError('');
    setFoundEmployee(null);

    try {
      const emp = await employeesApi.getByNumber(employeeId.trim().toUpperCase());
      setFoundEmployee(emp);
      
      // Check if employee has a passcode set
      if (!emp.has_passcode) {
        // First time - need to set passcode
        setStep('setPasscode');
      } else {
        // Has passcode - need to verify
        setStep('passcode');
      }
    } catch (err) {
      setError('Employee not found. Please check your ID and try again.');
    }
  };

  const handleManagerLogin = async () => {
    if (!managerEmail || !managerPassword) {
      setError('Please enter your email and password');
      return;
    }
    setError('');
    setIsProcessing(true);

    try {
      // Authenticate manager
      const authResult = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: managerEmail, password: managerPassword })
      });
      
      if (!authResult.ok) {
        const err = await authResult.json();
        throw new Error(err.detail || 'Invalid credentials');
      }
      
      const user = await authResult.json();
      
      let emp = null;
      
      // First try to get the linked employee
      if (user.linked_employee_id) {
        try {
          const empResult = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/employees/${user.linked_employee_id}`, {
            headers: { 'Authorization': `Bearer ${user.access_token}` }
          });
          if (empResult.ok) {
            emp = await empResult.json();
          }
        } catch (e) {
          console.warn('Failed to fetch linked employee:', e);
        }
      }
      
      // If no linked employee, try to find by email (manager might have been created as employee)
      if (!emp) {
        try {
          const empByEmailResult = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/employees/by-email/${encodeURIComponent(managerEmail)}`, {
            headers: { 'Authorization': `Bearer ${user.access_token}` }
          });
          if (empByEmailResult.ok) {
            emp = await empByEmailResult.json();
          }
        } catch (e) {
          console.warn('Failed to fetch employee by email:', e);
        }
      }
      
      // If still no employee found, create one automatically for managers
      if (!emp && (user.user?.role === 'manager' || user.user?.role === 'assistant_manager' ||
                   user.user?.roles?.includes('manager') || user.user?.roles?.includes('assistant_manager'))) {
        // Get the manager's location to create employee record
        const managedLocations = user.user?.managed_locations || [];
        const managedLocation = user.user?.managed_location;
        const primaryLocation = managedLocations[0] || managedLocation;
        
        if (primaryLocation) {
          // Try to auto-create the employee record
          try {
            const createResult = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/employees/create-for-manager`, {
              method: 'POST',
              headers: { 
                'Authorization': `Bearer ${user.access_token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ user_id: user.user?.id })
            });
            if (createResult.ok) {
              emp = await createResult.json();
            }
          } catch (e) {
            console.warn('Failed to auto-create employee record:', e);
          }
        }
      }
      
      if (!emp) {
        throw new Error('Your account is not linked to an employee record. Please contact your administrator.');
      }
      
      setFoundEmployee({
        ...emp,
        has_passcode: true, // Manager uses password, not passcode
        is_manager: true
      });
      setPasscode('MANAGER_AUTH'); // Special marker for manager auth
      
      // Check for active entry
      await checkActiveEntry(emp.employee_number);
      setStep('action');
    } catch (err) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSetPasscode = async () => {
    if (newPasscode.length < 4) {
      setError('Passcode must be at least 4 characters');
      return;
    }
    if (newPasscode !== confirmPasscode) {
      setError('Passcodes do not match');
      return;
    }
    
    setError('');
    setIsProcessing(true);
    
    try {
      await employeesApi.setPasscode(foundEmployee.employee_number, newPasscode);
      setPasscode(newPasscode);
      // Check for active entry after setting passcode
      await checkActiveEntry(foundEmployee.employee_number);
      setStep('action');
    } catch (err) {
      setError(err.message || 'Failed to set passcode');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleVerifyPasscode = async () => {
    if (!passcode) {
      setError('Please enter your passcode');
      return;
    }
    
    setError('');
    setIsProcessing(true);
    
    try {
      await employeesApi.verifyPasscode(foundEmployee.employee_number, passcode);
      // Check if employee has an active clock-in
      await checkActiveEntry(foundEmployee.employee_number);
      setStep('action');
    } catch (err) {
      setError(err.message || 'Invalid passcode');
    } finally {
      setIsProcessing(false);
    }
  };

  // Check for active time entry and break status
  const checkActiveEntry = async (empNumber) => {
    try {
      const result = await timeEntriesApi.getActiveEntry(empNumber);
      if (result.active) {
        setActiveEntry(result.entry);
        setOnBreak(result.on_break);
        setBreakCount(result.break_count || 0);
        setTotalBreakMinutes(result.total_break_minutes || 0);
      } else {
        setActiveEntry(null);
        setOnBreak(false);
        setBreakCount(0);
        setTotalBreakMinutes(0);
      }
    } catch (err) {
      console.error('Error checking active entry:', err);
      setActiveEntry(null);
    }
  };

  // Handle break start
  const handleStartBreak = async () => {
    setIsProcessing(true);
    setError('');
    try {
      const result = await timeEntriesApi.startBreak(foundEmployee.employee_number, passcode);
      setOnBreak(true);
      setBreakCount(prev => prev + 1);
      setSuccessMessage(`Break started at ${fmtTime(new Date())}\nEnjoy your break, ${foundEmployee.name}!`);
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        // Refresh active entry
        checkActiveEntry(foundEmployee.employee_number);
      }, 3000);
    } catch (err) {
      setError(err.message || 'Failed to start break');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle break end
  const handleEndBreak = async () => {
    setIsProcessing(true);
    setError('');
    try {
      const result = await timeEntriesApi.endBreak(foundEmployee.employee_number, passcode);
      setOnBreak(false);
      setTotalBreakMinutes(result.total_break_minutes || 0);
      setSuccessMessage(`Break ended\nBreak duration: ${result.break_duration} minutes\nTotal break time today: ${result.total_break_minutes} minutes`);
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        // Refresh active entry
        checkActiveEntry(foundEmployee.employee_number);
      }, 3000);
    } catch (err) {
      setError(err.message || 'Failed to end break');
    } finally {
      setIsProcessing(false);
    }
  };

  // Start continuous face detection when camera is ready
  const startFaceDetection = useCallback(() => {
    if (!faceModelLoaded || !videoRef.current) return;
    
    // Clear any existing interval
    if (faceDetectionIntervalRef.current) {
      clearInterval(faceDetectionIntervalRef.current);
    }
    
    setDetectingFace(true);
    
    faceDetectionIntervalRef.current = setInterval(async () => {
      if (videoRef.current && videoRef.current.readyState === 4) {
        try {
          const detections = await faceapi.detectAllFaces(
            videoRef.current,
            new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.5 })
          );
          setFaceDetected(detections.length > 0);
        } catch (err) {
          console.warn('Face detection error:', err);
        }
      }
    }, 300); // Check every 300ms
  }, [faceModelLoaded]);

  const stopFaceDetection = useCallback(() => {
    if (faceDetectionIntervalRef.current) {
      clearInterval(faceDetectionIntervalRef.current);
      faceDetectionIntervalRef.current = null;
    }
    setDetectingFace(false);
    setFaceDetected(false);
  }, []);

  const startCamera = useCallback(async (action) => {
    setClockAction(action);
    setStep('camera');
    setCameraError('');
    setCameraReady(false);
    setCapturedPhoto(null);
    setCameraUnavailable(false);
    setFaceDetected(false);

    try {
      // First check if any video input devices exist
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(d => d.kind === 'videoinput');
      
      if (videoDevices.length === 0) {
        // No camera found - allow clock without photo
        setCameraUnavailable(true);
        setCameraError('No camera detected on this device. You can still clock in/out without a photo.');
        return;
      }

      const constraints = {
        video: {
          facingMode: 'user',
          width: { ideal: 640 },
          height: { ideal: 480 }
        },
        audio: false
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play();
          setCameraReady(true);
          // Start face detection after camera is ready
          startFaceDetection();
        };
      }
    } catch (err) {
      console.error('Camera error:', err);
      // Handle different camera errors
      if (err.name === 'NotAllowedError') {
        setCameraUnavailable(true);
        setCameraError('Camera access was denied. You can still clock in/out without a photo, but a notice will be sent to your manager.');
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setCameraUnavailable(true);
        setCameraError('No camera found on this device. You can still clock in/out without a photo.');
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        setCameraUnavailable(true);
        setCameraError('Camera is being used by another application. You can still clock in/out without a photo.');
      } else {
        setCameraUnavailable(true);
        setCameraError('Unable to access camera. You can still clock in/out without a photo.');
      }
    }
  }, [startFaceDetection]);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current || !cameraReady) return;
    
    // Stop face detection before capturing
    stopFaceDetection();
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    const photo = canvas.toDataURL('image/jpeg', 0.8);
    setCapturedPhoto(photo);
    stopCamera();
  }, [cameraReady, stopFaceDetection]);

  const stopCamera = useCallback(() => {
    stopFaceDetection();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
      });
      streamRef.current = null;
    }
    setCameraReady(false);
  }, [stopFaceDetection]);

  const handleClockAction = async () => {
    // Photo is required unless camera is unavailable
    if (!capturedPhoto && !cameraUnavailable) {
      setError('Photo is required');
      return;
    }
    
    setIsProcessing(true);
    setError('');
    
    try {
      let result;
      if (clockAction === 'in') {
        result = await timeEntriesApi.clockIn(
          foundEmployee.employee_number, 
          passcode, 
          capturedPhoto, 
          cameraUnavailable  // Pass camera unavailable flag
        );
        let msg = `Clocked in at ${fmtTime(new Date())}\nLocation: ${result.location_name || foundEmployee.location_name}`;
        if (cameraUnavailable) {
          msg += '\n\n⚠️ No photo taken - camera unavailable';
        }
        setSuccessMessage(msg);
      } else {
        result = await timeEntriesApi.clockOut(
          foundEmployee.employee_number, 
          passcode, 
          capturedPhoto,
          cameraUnavailable  // Pass camera unavailable flag
        );
        let msg = `Clocked out at ${fmtTime(new Date())}\nLocation: ${result.location_name || foundEmployee.location_name}\nHours worked: ${result.hours_worked?.toFixed(2) || '0'}`;
        if (cameraUnavailable) {
          msg += '\n\n⚠️ No photo taken - camera unavailable';
        }
        setSuccessMessage(msg);
      }
      
      setShowSuccess(true);
      
      setTimeout(() => {
        resetKiosk();
        setShowSuccess(false);
      }, 5000);
    } catch (err) {
      setError(err.message || 'Failed to process clock action');
    } finally {
      setIsProcessing(false);
    }
  };

  const retakePhoto = () => {
    setCapturedPhoto(null);
    startCamera(clockAction);
  };

  const resetKiosk = () => {
    setFoundEmployee(null);
    setEmployeeId('');
    setPasscode('');
    setNewPasscode('');
    setConfirmPasscode('');
    setError('');
    setCapturedPhoto(null);
    setClockAction(null);
    setCameraError('');
    setCameraUnavailable(false);
    setFaceDetected(false);
    setActiveEntry(null);
    setOnBreak(false);
    setBreakCount(0);
    setTotalBreakMinutes(0);
    setManagerEmail('');
    setManagerPassword('');
    setStep('lookup');
    stopCamera();
  };

  const goBack = () => {
    setError('');
    if (step === 'camera') {
      stopCamera();
      setStep('action');
    } else if (step === 'action') {
      if (foundEmployee?.is_manager) {
        setStep('managerLogin');
      } else {
        setStep('passcode');
      }
      setPasscode('');
    } else if (step === 'passcode' || step === 'setPasscode' || step === 'managerLogin') {
      setStep('lookup');
      setFoundEmployee(null);
      setPasscode('');
      setNewPasscode('');
      setConfirmPasscode('');
      setManagerEmail('');
      setManagerPassword('');
    }
  };

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 to-violet-800 flex items-center justify-center p-6">
        <Card className="w-full max-w-md border-0 shadow-2xl">
          <CardContent className="p-12 text-center">
            <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-purple-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Success!</h2>
            <p className="text-gray-600 whitespace-pre-line">{successMessage}</p>
            <p className="text-lg font-medium text-purple-600 mt-4">Have a great day, {foundEmployee?.name}!</p>
            {capturedPhoto && (
              <div className="mt-4">
                <p className="text-sm text-gray-500 mb-2">Photo verified:</p>
                <img src={capturedPhoto} alt="Verification" className="w-24 h-24 object-cover rounded-lg mx-auto border-2 border-purple-200" />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-violet-900 flex items-center justify-center p-6">
      <Card className="w-full max-w-lg border-0 shadow-2xl">
        <CardContent className="p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <img src={LOGO_URL} alt="STP TeamBase Logo" className="w-16 h-16 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-gray-900">STP TeamBase</h1>
            <p className="text-gray-500 mt-1">Clock In / Clock Out</p>
          </div>

          {/* Current Time */}
          <div className="text-center mb-8 p-6 bg-purple-50 rounded-xl">
            <p className="text-5xl font-bold text-purple-900 font-mono">{fmtTime.preference === '24h' ? format(currentTime, 'HH:mm:ss') : format(currentTime, 'h:mm:ss a')}</p>
            <p className="text-gray-500 mt-2">{format(currentTime, 'EEEE, MMMM d, yyyy')}</p>
          </div>

          {/* Step 1: Employee ID Lookup */}
          {step === 'lookup' && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Employee ID</label>
                <Input
                  placeholder="Enter your Employee ID (e.g., DT-001)"
                  value={employeeId}
                  onChange={(e) => setEmployeeId(e.target.value.toUpperCase())}
                  onKeyPress={(e) => e.key === 'Enter' && handleLookup()}
                  className="h-12 text-lg font-mono bg-purple-50 border-purple-200 focus:ring-purple-500"
                  data-testid="kiosk-employee-id"
                />
              </div>

              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              <Button onClick={handleLookup} className="w-full h-12 bg-purple-600 hover:bg-purple-700" data-testid="lookup-btn">
                <User className="w-4 h-4 mr-2" />
                Look Up Employee
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-400">Or</span>
                </div>
              </div>

              <Button 
                variant="outline" 
                onClick={() => setStep('managerLogin')} 
                className="w-full h-12"
                data-testid="manager-login-btn"
              >
                <Lock className="w-4 h-4 mr-2" />
                Manager / Supervisor Login
              </Button>
            </div>
          )}

          {/* Manager Login Step */}
          {step === 'managerLogin' && (
            <div className="space-y-4">
              <Button variant="ghost" onClick={goBack} className="mb-2 text-purple-600 hover:text-purple-700">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>

              <div className="text-center mb-4">
                <Lock className="w-12 h-12 mx-auto text-purple-600 mb-2" />
                <h3 className="text-lg font-semibold text-gray-900">Manager Login</h3>
                <p className="text-sm text-gray-500">Use your account credentials</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Email</label>
                <Input
                  type="email"
                  placeholder="your.email@company.com"
                  value={managerEmail}
                  onChange={(e) => setManagerEmail(e.target.value)}
                  className="h-12 bg-purple-50 border-purple-200"
                  data-testid="manager-email"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Password</label>
                <div className="relative">
                  <Input
                    type={showPasscode ? 'text' : 'password'}
                    placeholder="Enter password"
                    value={managerPassword}
                    onChange={(e) => setManagerPassword(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleManagerLogin()}
                    className="h-12 bg-purple-50 border-purple-200 pr-12"
                    data-testid="manager-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasscode(!showPasscode)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"
                  >
                    {showPasscode ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              <Button 
                onClick={handleManagerLogin} 
                className="w-full h-12 bg-purple-600 hover:bg-purple-700" 
                disabled={isProcessing}
                data-testid="manager-login-submit-btn"
              >
                {isProcessing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                Sign In & Continue
              </Button>
            </div>
          )}

          {/* Step 2a: Set Passcode (First Time) */}
          {step === 'setPasscode' && foundEmployee && (
            <div className="space-y-4">
              <Button variant="ghost" onClick={goBack} className="mb-2 text-purple-600 hover:text-purple-700">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>

              <div className="flex items-center gap-4 p-4 bg-purple-50 rounded-xl">
                <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center">
                  <User className="w-7 h-7 text-purple-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-lg">{foundEmployee.name}</p>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Building2 className="w-4 h-4" />
                    {foundEmployee.location_name}
                  </div>
                  <span className="text-xs font-mono bg-purple-200 text-purple-800 px-2 py-1 rounded mt-1 inline-block">
                    {foundEmployee.employee_number}
                  </span>
                </div>
              </div>

              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <p className="text-amber-800 text-sm font-medium mb-1">First Time Setup</p>
                <p className="text-amber-700 text-sm">Please create a personal passcode for clocking in and out.</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Create Passcode</label>
                <div className="relative">
                  <Input
                    type={showPasscode ? "text" : "password"}
                    placeholder="Enter at least 4 characters"
                    value={newPasscode}
                    onChange={(e) => setNewPasscode(e.target.value)}
                    className="h-12 pr-10 bg-purple-50 border-purple-200 focus:ring-purple-500"
                    data-testid="new-passcode"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasscode(!showPasscode)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                  >
                    {showPasscode ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Confirm Passcode</label>
                <Input
                  type={showPasscode ? "text" : "password"}
                  placeholder="Re-enter passcode"
                  value={confirmPasscode}
                  onChange={(e) => setConfirmPasscode(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSetPasscode()}
                  className="h-12 bg-purple-50 border-purple-200 focus:ring-purple-500"
                  data-testid="confirm-passcode"
                />
              </div>

              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              <Button 
                onClick={handleSetPasscode} 
                className="w-full h-12 bg-purple-600 hover:bg-purple-700" 
                disabled={isProcessing}
                data-testid="set-passcode-btn"
              >
                {isProcessing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                Set Passcode & Continue
              </Button>
            </div>
          )}

          {/* Step 2b: Verify Passcode */}
          {step === 'passcode' && foundEmployee && (
            <div className="space-y-4">
              <Button variant="ghost" onClick={goBack} className="mb-2 text-purple-600 hover:text-purple-700">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>

              <div className="flex items-center gap-4 p-4 bg-purple-50 rounded-xl">
                <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center">
                  <User className="w-7 h-7 text-purple-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-lg">{foundEmployee.name}</p>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Building2 className="w-4 h-4" />
                    {foundEmployee.location_name}
                  </div>
                  <span className="text-xs font-mono bg-purple-200 text-purple-800 px-2 py-1 rounded mt-1 inline-block">
                    {foundEmployee.employee_number}
                  </span>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Enter Your Passcode</label>
                <div className="relative">
                  <Input
                    type={showPasscode ? "text" : "password"}
                    placeholder="Enter passcode"
                    value={passcode}
                    onChange={(e) => setPasscode(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleVerifyPasscode()}
                    className="h-12 pr-10 bg-purple-50 border-purple-200 focus:ring-purple-500"
                    data-testid="passcode-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasscode(!showPasscode)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                  >
                    {showPasscode ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              <Button 
                onClick={handleVerifyPasscode} 
                className="w-full h-12 bg-purple-600 hover:bg-purple-700" 
                disabled={isProcessing}
                data-testid="verify-passcode-btn"
              >
                {isProcessing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                Verify & Continue
              </Button>
            </div>
          )}

          {/* Step 3: Clock Action */}
          {step === 'action' && foundEmployee && (
            <div className="space-y-6">
              <Button variant="ghost" onClick={goBack} className="mb-2 text-purple-600 hover:text-purple-700">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>

              <div className="flex items-center gap-4 p-4 bg-purple-50 rounded-xl">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-7 h-7 text-green-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-lg">{foundEmployee.name}</p>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Building2 className="w-4 h-4" />
                    {foundEmployee.location_name}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs bg-green-200 text-green-800 px-2 py-1 rounded">
                      ✓ Verified
                    </span>
                    {activeEntry && (
                      <Badge className={onBreak ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}>
                        {onBreak ? "On Break" : "Clocked In"}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Active shift info */}
              {activeEntry && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-blue-700">
                      <Clock className="w-4 h-4 inline mr-1" />
                      Clocked in: {fmtTime(activeEntry.clock_in)}
                    </span>
                    {breakCount > 0 && (
                      <span className="text-blue-600">
                        <Coffee className="w-4 h-4 inline mr-1" />
                        Breaks: {breakCount} ({totalBreakMinutes} min)
                      </span>
                    )}
                  </div>
                </div>
              )}

              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              <p className="text-center text-gray-600">Select an action:</p>

              {/* Clock In/Out buttons - show based on active entry */}
              {!activeEntry ? (
                // Not clocked in - show Clock In button
                <Button 
                  onClick={() => startCamera('in')} 
                  className="w-full h-20 bg-purple-600 hover:bg-purple-700 text-lg flex flex-col gap-2" 
                  data-testid="clock-in-btn"
                >
                  <LogIn className="w-8 h-8" />
                  Clock In
                </Button>
              ) : (
                // Clocked in - show break and clock out options
                <div className="space-y-3">
                  {/* Break buttons */}
                  <div className="grid grid-cols-2 gap-3">
                    {!onBreak ? (
                      <Button 
                        onClick={handleStartBreak}
                        disabled={isProcessing}
                        className="h-16 bg-amber-500 hover:bg-amber-600 text-white flex flex-col gap-1"
                        data-testid="start-break-btn"
                      >
                        {isProcessing ? <Loader2 className="w-6 h-6 animate-spin" /> : <Coffee className="w-6 h-6" />}
                        Start Break
                      </Button>
                    ) : (
                      <Button 
                        onClick={handleEndBreak}
                        disabled={isProcessing}
                        className="h-16 bg-green-500 hover:bg-green-600 text-white flex flex-col gap-1"
                        data-testid="end-break-btn"
                      >
                        {isProcessing ? <Loader2 className="w-6 h-6 animate-spin" /> : <Play className="w-6 h-6" />}
                        End Break
                      </Button>
                    )}
                    <Button 
                      onClick={() => startCamera('out')}
                      disabled={onBreak}
                      className="h-16 bg-violet-600 hover:bg-violet-700 text-white flex flex-col gap-1 disabled:opacity-50"
                      data-testid="clock-out-btn"
                    >
                      <LogOut className="w-6 h-6" />
                      Clock Out
                    </Button>
                  </div>
                  
                  {onBreak && (
                    <p className="text-center text-sm text-amber-600">
                      <Pause className="w-4 h-4 inline mr-1" />
                      End your break before clocking out
                    </p>
                  )}
                </div>
              )}

              <p className="text-center text-sm text-gray-500">
                <Camera className="w-4 h-4 inline mr-1" />
                Photo verification required for clock in/out
              </p>
            </div>
          )}

          {/* Step 4: Camera - Photo Required */}
          {step === 'camera' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Button variant="ghost" onClick={goBack} className="text-purple-600 hover:text-purple-700">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                <span className="text-sm font-medium text-gray-600">
                  {clockAction === 'in' ? 'Clock In' : 'Clock Out'} Photo
                </span>
              </div>

              {!capturedPhoto ? (
                <>
                  <div className="relative aspect-video bg-gray-900 rounded-lg overflow-hidden">
                    {cameraError ? (
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-4">
                        <AlertCircle className={`w-12 h-12 mb-3 ${cameraUnavailable ? 'text-amber-400' : 'text-red-400'}`} />
                        <p className="text-center text-sm">{cameraError}</p>
                      </div>
                    ) : !cameraReady ? (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
                        <span className="ml-2 text-white">Starting camera...</span>
                      </div>
                    ) : null}
                    <video 
                      ref={videoRef} 
                      autoPlay 
                      playsInline 
                      muted 
                      className={`w-full h-full object-cover ${cameraReady ? '' : 'opacity-0'}`}
                    />
                    {cameraReady && (
                      <>
                        {/* Face detection frame overlay */}
                        <div className={`absolute inset-0 border-4 border-dashed m-8 rounded-lg pointer-events-none transition-colors duration-300 ${
                          faceDetected ? 'border-green-400' : 'border-purple-400'
                        }`}></div>
                        
                        {/* Face detection status indicator */}
                        {faceModelLoaded && (
                          <div className={`absolute top-2 left-2 px-2 py-1 rounded text-xs flex items-center gap-1 transition-colors duration-300 ${
                            faceDetected ? 'bg-green-500 text-white' : 'bg-amber-500 text-white'
                          }`}>
                            <UserCheck className="w-3 h-3" />
                            {faceDetected ? 'Face detected' : 'Position your face'}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  <canvas ref={canvasRef} className="hidden" />
                  
                  {cameraReady && (
                    <>
                      {/* Face detection hint */}
                      {faceModelLoaded ? (
                        <div className={`p-2 rounded-lg text-sm text-center ${
                          faceDetected ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
                        }`}>
                          {faceDetected 
                            ? '✓ Face detected - Ready to capture!' 
                            : 'Please position your face clearly in the frame'}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 text-center">
                          Position your face in the frame and click capture
                        </p>
                      )}
                      
                      <Button 
                        onClick={capturePhoto} 
                        className={`w-full h-12 ${faceModelLoaded && !faceDetected 
                          ? 'bg-amber-500 hover:bg-amber-600' 
                          : 'bg-purple-600 hover:bg-purple-700'}`}
                        data-testid="capture-photo-btn"
                      >
                        <Camera className="w-4 h-4 mr-2" />
                        {faceModelLoaded && !faceDetected ? 'Capture Anyway (No Face Detected)' : 'Capture Photo'}
                      </Button>
                    </>
                  )}
                  
                  {cameraError && !cameraUnavailable && (
                    <Button 
                      onClick={() => startCamera(clockAction)} 
                      className="w-full h-12 bg-purple-600 hover:bg-purple-700"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Try Again
                    </Button>
                  )}

                  {/* Camera unavailable - allow continue without photo */}
                  {cameraUnavailable && (
                    <div className="space-y-3">
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-amber-700 text-sm">
                          <AlertCircle className="w-4 h-4 inline mr-1" />
                          A notice will be sent to your manager that you clocked {clockAction} without a photo.
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          onClick={() => startCamera(clockAction)} 
                          variant="outline"
                          className="flex-1"
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Try Again
                        </Button>
                        <Button 
                          onClick={handleClockAction} 
                          className="flex-1 bg-amber-600 hover:bg-amber-700"
                          disabled={isProcessing}
                          data-testid="continue-without-photo-btn"
                        >
                          {isProcessing ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <CheckCircle className="w-4 h-4 mr-2" />
                          )}
                          Continue Without Photo
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="relative aspect-video bg-gray-900 rounded-lg overflow-hidden">
                    <img src={capturedPhoto} alt="Captured" className="w-full h-full object-cover" />
                    <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded text-xs flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> Photo captured
                    </div>
                  </div>

                  {error && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                      <p className="text-red-600 text-sm">{error}</p>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button onClick={retakePhoto} variant="outline" className="flex-1">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Retake
                    </Button>
                    <Button 
                      onClick={handleClockAction} 
                      className="flex-1 bg-purple-600 hover:bg-purple-700" 
                      disabled={isProcessing}
                      data-testid="confirm-clock-btn"
                    >
                      {isProcessing ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <CheckCircle className="w-4 h-4 mr-2" />
                      )}
                      Confirm {clockAction === 'in' ? 'Clock In' : 'Clock Out'}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Kiosk;
